import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./index.module.scss";
import useOutsideClick from "../../hooks/useOutsideClick";

const TopicItem = ({ onClick: onTopicClick, currentTopicTitle, title }) => (
	<li
		className={`${styles.topicItem} ${currentTopicTitle === title ? styles.activeTopicItem : ""}`}
		onClick={() => onTopicClick(title)}
		onKeyUp={(e) => {
			if (e.key === "Enter") onTopicClick(title);
		}}
	>
		{currentTopicTitle === title && (
			<div className={styles.activeIcon}>
				<svg
					width="8"
					height="9"
					viewBox="0 0 8 9"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<title>active icon</title>
					<circle cx="4" cy="4.5" r="4" fill="#8260C5" />
				</svg>
			</div>
		)}
		<span> {title}</span>
	</li>
);

const DesktopSidebar = ({
	groupedTopics,
	activeIndex,
	toggleItem,
	currentTopicTitle,
	onTopicClick,
}) => (
	<ul className={styles.themeList}>
		{Object.keys(groupedTopics).map((theme, index) => (
			<li key={theme}>
				<button
					className={styles.themeTitle}
					type="button"
					onClick={() => toggleItem(index)}
				>
					{theme}
					<span
						className={
							activeIndex === index ? styles.arrowUp : styles.arrowDown
						}
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<title>展開/收合</title>
							<path
								d="M17.9997 15.5C17.8681 15.5008 17.7376 15.4756 17.6158 15.4258C17.494 15.3761 17.3831 15.3027 17.2897 15.21L12.7097 10.6201C12.6167 10.5263 12.5061 10.4519 12.3843 10.4012C12.2624 10.3504 12.1317 10.3243 11.9997 10.3243C11.8677 10.3243 11.737 10.3504 11.6151 10.4012C11.4933 10.4519 11.3827 10.5263 11.2897 10.6201L6.7097 15.21C6.52139 15.3984 6.266 15.5041 5.9997 15.5041C5.7334 15.5041 5.478 15.3984 5.2897 15.21C5.10139 15.0217 4.99561 14.7664 4.99561 14.5001C4.99561 14.3682 5.02158 14.2376 5.07204 14.1158C5.1225 13.994 5.19646 13.8833 5.2897 13.7901L9.8797 9.21006C10.4496 8.6625 11.2093 8.35669 11.9997 8.35669C12.7901 8.35669 13.5498 8.6625 14.1197 9.21006L18.7097 13.7901C18.8034 13.883 18.8778 13.9936 18.9286 14.1155C18.9794 14.2373 19.0055 14.368 19.0055 14.5001C19.0055 14.6321 18.9794 14.7628 18.9286 14.8846C18.8778 15.0065 18.8034 15.1171 18.7097 15.21C18.6163 15.3027 18.5054 15.3761 18.3836 15.4258C18.2618 15.4756 18.1313 15.5008 17.9997 15.5Z"
								fill="#442278"
							/>
						</svg>
					</span>
				</button>

				<ul
					className={
						activeIndex === index ? styles.contentActive : styles.content
					}
				>
					{groupedTopics[theme].map(({ title }) => (
						<TopicItem
							key={title}
							title={title}
							currentTopicTitle={currentTopicTitle}
							onClick={onTopicClick}
						/>
					))}
				</ul>
			</li>
		))}
	</ul>
);
function transformData(data) {
	return Object.keys(data).map((group) => ({
		label: group,
		items: data[group].map((item) => ({
			key: item.key,
			title: item.title,
		})),
	}));
}

const MobileSidebar = ({
	id = "mobile-sidebar",
	groupedTopics,
	currentTopicTitle,
	onTopicClick,
}) => {
	const data = transformData(groupedTopics);

	const [isOpen, setIsOpen] = useState(false);
	const activeItemRef = useRef(null);

	const handleChange = (item) => {
		onTopicClick(item.title);
		setIsOpen(false);
	};

	const dropdownRef = useRef(null);

	useOutsideClick({
		ref: dropdownRef,
		handler: () => setIsOpen(false),
	});

	useEffect(() => {
		if (isOpen && activeItemRef.current) {
			setTimeout(() => {
				activeItemRef.current.scrollIntoView({
					behavior: "auto",
					block: "nearest",
				});
			}, 0);
		}
	}, [isOpen]);

	return (
		<div ref={dropdownRef} className={styles.mobileSidebar}>
			<button
				id={id}
				aria-label="Toggle dropdown"
				aria-haspopup="true"
				aria-expanded={isOpen}
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={styles.dropdownBtn}
			>
				<span>{currentTopicTitle}</span>
				<svg
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className={`${styles.arrow} 
            ${!isOpen && styles.arrowDown}`}
				>
					<title>展開/收合</title>
					<path
						d="M3.52695 9.86091C3.58893 9.92339 3.66266 9.97299 3.7439 10.0068C3.82514 10.0407 3.91228 10.0581 4.00029 10.0581C4.08829 10.0581 4.17543 10.0407 4.25667 10.0068C4.33791 9.97299 4.41164 9.92339 4.47362 9.86091L7.52695 6.80758C7.58893 6.74509 7.66266 6.6955 7.7439 6.66165C7.82514 6.62781 7.91228 6.61038 8.00029 6.61038C8.08829 6.61038 8.17543 6.62781 8.25667 6.66165C8.33791 6.6955 8.41164 6.74509 8.47362 6.80758L11.527 9.86091C11.5889 9.92339 11.6627 9.97299 11.7439 10.0068C11.8251 10.0407 11.9123 10.0581 12.0003 10.0581C12.0883 10.0581 12.1754 10.0407 12.2567 10.0068C12.3379 9.97299 12.4116 9.92339 12.4736 9.86091C12.5978 9.736 12.6675 9.56703 12.6675 9.39091C12.6675 9.21478 12.5978 9.04582 12.4736 8.92091L9.41362 5.86091C9.03862 5.48638 8.53029 5.27601 8.00029 5.27601C7.47028 5.27601 6.96195 5.48638 6.58695 5.86091L3.52695 8.92091C3.40279 9.04582 3.33309 9.21478 3.33309 9.39091C3.33309 9.56703 3.40279 9.736 3.52695 9.86091Z"
						fill="#A479E9"
					/>
				</svg>
			</button>
			{/* Open */}
			{isOpen && (
				<div aria-label="Dropdown menu" className={styles.dropdown}>
					<ul role="menu" aria-labelledby={id} aria-orientation="vertical">
						{data.map((item) => (
							<li key={item.label} className={styles.themeGroup}>
								<span className={styles.dropdownGroupTitle}>{item.label}</span>
								{
									<ul>
										{item.items.map((topic) => (
											<li
												key={topic.key}
												className={`${styles.listItem} ${topic.title === currentTopicTitle ? styles.activeItem : ""}`}
												ref={
													topic.title === currentTopicTitle
														? activeItemRef
														: null
												}
												onClick={() => handleChange(topic)}
												onKeyDown={(e) => {
													if (e.key === "Enter" || e.key === " ")
														handleChange(topic);
												}}
											>
												<span>{topic.title}</span>
											</li>
										))}
									</ul>
								}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

const TopicsSidebar = ({ topics, currentTopicTitle, onTopicClick }) => {
	const [activeIndex, setActiveIndex] = useState(0);

	const toggleItem = (index) => {
		setActiveIndex(activeIndex === index ? null : index);
	};
	const groupedTopics = useMemo(
		() => Object.groupBy(topics, ({ theme }) => theme),
		[topics],
	);

	useEffect(() => {
		if (currentTopicTitle) {
			setActiveIndex(
				Object.values(groupedTopics).findIndex((topics) =>
					topics.find(({ title }) => title === currentTopicTitle),
				),
			);
		}
	}, [currentTopicTitle, groupedTopics]);

	return (
		<aside className={styles.sidebar}>
			<MobileSidebar
				groupedTopics={groupedTopics}
				currentTopicTitle={currentTopicTitle}
				onTopicClick={onTopicClick}
			/>
			<DesktopSidebar
				groupedTopics={groupedTopics}
				activeIndex={activeIndex}
				toggleItem={toggleItem}
				currentTopicTitle={currentTopicTitle}
				onTopicClick={onTopicClick}
			/>
		</aside>
	);
};

export default TopicsSidebar;
